import React, { useCallback, useMemo, useState } from "react";
import {
  OutlinedButton,
  ToastError,
  createClasses,
} from "@bloomberg/media-ui-core";
import { ArrowRight } from "@bloomberg/media-ui-icons";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./watchlistBundle.module.css";
import { savePortfolio } from "~utils/portfolio/createPortfolio";
import { PortfolioForm, PortfolioFormHolding } from "~types/portfolio";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { HEADLINE_TRACKING } from "~components/WatchlistBundleContainer";
import { PaywallButtonContainer } from "~components/PaywallButtonContainer";
import {
  THREE_SECONDS,
  FOUR_SECONDS,
  COMMON_PORTFOLIO_FORM_VALUES,
} from "~constants";
import { WatchlistBundleStyleVariant } from "~types/types";
import { usePaywallAction } from "~hooks/usePaywallAction";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";

export type BundleSecurity = {
  ticker: string;
  name: string;
};

type BundleProps = {
  headline: string;
  banner: string;
  securities: BundleSecurity[];
  maxWatchlistCountReached?: boolean;
  tariffBundlesFeatureEnabled?: boolean;
  variant?: WatchlistBundleStyleVariant;
};

const getEmptyLots = () => [
  {
    lotId: -1,
    shares: "0",
    buyPrice: "0",
    buyDate: new Date().getTime(),
  },
];

function getHoldingsForBundle(
  bundleSecurities: BundleSecurity[]
): PortfolioFormHolding[] {
  const lots = getEmptyLots();

  return bundleSecurities.map((security) => ({
    ticker: security.ticker,
    companyName: security.name,
    totalShares: 0,
    costBasis: 0.0,
    lots,
  }));
}

export default function WatchlistBundle(props: BundleProps) {
  const {
    headline,
    banner,
    securities: bundleSecurities,
    maxWatchlistCountReached,
    tariffBundlesFeatureEnabled,
    variant = "default",
  } = props;
  const { client, session } = useRegUIClient();
  const { upsertPortfolio } = usePortfolioPage();
  const [shouldShowError, setShouldShowError] = useState(false);
  const showError = useCallback(() => setShouldShowError(true), []);
  const hideError = useCallback(() => setShouldShowError(false), []);
  const trackingConfig = useMemo(() => HEADLINE_TRACKING[headline], [headline]);
  const bundlePortfolio: PortfolioForm = useMemo(
    () => ({
      ...COMMON_PORTFOLIO_FORM_VALUES,
      name: headline,
      holdings: getHoldingsForBundle(bundleSecurities),
    }),
    [headline, bundleSecurities]
  );
  const [creatingPortfolio, setCreatingPortfolio] = useState(false);
  const bconnectId = session?.userInfo?.userId ?? "";

  const { shouldWallAction, blockUserIfWalledActionIsAttempted } =
    usePaywallAction();

  const handleLoginRedirect = useCallback(() => {
    if (!session?.isLoggedIn && client) {
      sessionStorage.setItem(
        "createBundleAfterLogin",
        JSON.stringify({ shouldCreate: true, bundlePortfolio })
      );

      client.linkToSignIn();
      trackClickEvent("watchlistLogIn", "login", "click", "watchlist");

      return true;
    }

    return false;
  }, [session?.isLoggedIn, client, bundlePortfolio]);

  const handleStartListClick = useCallback(() => {
    hideError();

    if (shouldWallAction) {
      blockUserIfWalledActionIsAttempted();

      return;
    }

    // if user is not logged in and the paywall isn't enabled, display sign in modal
    if (handleLoginRedirect()) {
      return;
    }

    setCreatingPortfolio(true);

    trackClickEvent(...trackingConfig);

    trackClickEvent("watchlistEdit", "create_watchlist", "click", "watchlist");

    addPageAction("watchlistBundleCreate", {
      bundleName: headline,
    });

    savePortfolio(bundlePortfolio, bconnectId)
      .then(upsertPortfolio)
      .catch(() => {
        showError();
        setTimeout(() => hideError(), FOUR_SECONDS);
      })
      .finally(() => {
        setCreatingPortfolio(false);
      });
  }, [
    client,
    session,
    bundlePortfolio,
    trackingConfig,
    upsertPortfolio,
    showError,
    hideError,
    shouldWallAction,
    blockUserIfWalledActionIsAttempted,
  ]);

  const containerClass = createClasses(
    tariffBundlesFeatureEnabled ? styles.tariffBundle : styles.watchlistBundle,
    {
      [styles.wrappedBundleModifier]: variant === "wrapped",
    }
  );

  const buttonClass = createClasses(
    tariffBundlesFeatureEnabled ? styles.tariffButton : styles.startListButton,
    {
      [styles.wrappedButtonModifier]: variant === "wrapped",
    }
  );

  const mobileButtonClass = createClasses(
    tariffBundlesFeatureEnabled
      ? styles.tariffButtonMobile
      : styles.startListButtonMobile,
    {
      [styles.wrappedButtonModifier]: variant === "wrapped",
    }
  );

  return (
    <div className={containerClass}>
      <div className={styles.bundleHeadline}>{headline}</div>
      <div className={styles.bundleBanner}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img alt="" src={banner} />
      </div>
      <div className={styles.bundleSecurities}>
        {bundleSecurities?.map(({ ticker, name }) => (
          <div className={styles.securityRow} key={ticker}>
            <span className={styles.securityTicker}>{ticker}</span>
            <span className={styles.securityName} title={name}>
              {" "}
              {name}
            </span>
          </div>
        ))}
      </div>

      <PaywallButtonContainer
        disabled={variant !== "wrapped"}
        handleLoginRedirect={handleLoginRedirect}
      >
        <OutlinedButton
          title="Start this list"
          ActionIcon={<ArrowRight />}
          disabled={maxWatchlistCountReached}
          onClick={handleStartListClick}
          className={buttonClass}
          loading={creatingPortfolio}
        >
          <span>Start this list</span>
        </OutlinedButton>
        <OutlinedButton
          title="Start this list"
          size="small"
          ActionIcon={<ArrowRight />}
          onClick={handleStartListClick}
          className={mobileButtonClass}
          loading={creatingPortfolio}
        >
          <span>Start this list</span>
        </OutlinedButton>
      </PaywallButtonContainer>

      {shouldShowError && <ToastError open autoHideDuration={THREE_SECONDS} />}
    </div>
  );
}
